// SE-facing view of their mapped distributor's current stock. Reuses the
// same /distributor-stock endpoint the Distributor Portal's own stock
// screen uses (frontend/src/pages/portal/Stock.jsx), scoped to the SE's own
// mapped distributor(s). Read-only by default; editing on-hand qty is
// gated behind the per-user "EDIT_DISTRIBUTOR_STOCK" workflow permission.
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Search, Boxes, Loader2, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useHasPerm } from "@/lib/permissions";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function DistributorStockSE() {
  const { user } = useAuth();
  const canEdit = useHasPerm("EDIT_DISTRIBUTOR_STOCK");
  const nav = useNavigate();
  const [distributors, setDistributors] = useState([]);
  const [distributorId, setDistributorId] = useState("");
  const [items, setItems] = useState([]);
  const [totalKg, setTotalKg] = useState(0);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editVal, setEditVal] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    Promise.all([
      api.get(`/user-distributor-map/users/${user.id}/distributors`),
      // managers also see every distributor of their own sub-zone, not just the ones mapped to them
      ["ASM", "ZSM", "RSM", "SALES_HEAD"].includes(user.role_code)
        ? api.get("/distributors", { params: { scope: "all", page_size: 500, status: "active" } }).catch(() => null)
        : Promise.resolve(null),
      // Today's Beat's outlets carry their own distributor_id — default the
      // picker to whichever mapped distributor actually serves today's
      // planned beat, instead of leaving it blank whenever the SE has more
      // than one mapped distributor. Best-effort: any failure here just
      // falls back to the old "only auto-pick when there's exactly one"
      // behavior below.
      api.get("/beats/today").catch(() => null),
    ]).then(([distRes, zoneRes, beatRes]) => {
      const mappedList = (distRes.data.items || []).filter(d => d.distributor_is_active !== false && !d.distributor_missing);
      const have = new Set(mappedList.map(d => d.distributor_id));
      const extra = (zoneRes?.data?.items || [])
        .filter(d => !have.has(d.id))
        .map(d => ({ distributor_id: d.id, distributor_code: d.code, distributor_name: d.name }));
      const list = [...mappedList, ...extra].sort((a, b) => (a.distributor_code || "").localeCompare(b.distributor_code || ""));
      setDistributors(list);
      // beats/today rows are {outlet, status} — the distributor lives on the outlet.
      // Count them: the distributor serving most of today's beat wins (a beat can
      // contain a few outlets of another distributor).
      const counts = {};
      for (const r of beatRes?.data?.outlets || []) {
        const did = r?.outlet?.distributor_id;
        if (did) counts[did] = (counts[did] || 0) + 1;
      }
      const todaysMapped = [...list]
        .filter(d => counts[d.distributor_id])
        .sort((x, y) => counts[y.distributor_id] - counts[x.distributor_id])[0];
      if (todaysMapped) setDistributorId(todaysMapped.distributor_id);
      else if (list.length === 1) setDistributorId(list[0].distributor_id);
    }).catch(e => toast.error(formatApiError(e)));
  }, [user?.id]);

  const load = () => {
    if (!distributorId) { setItems([]); setTotalKg(0); return; }
    setLoading(true);
    api.get("/distributor-stock", { params: { distributor_id: distributorId, page_size: 500 } })
      .then(({ data }) => { setItems(data.items || []); setTotalKg(data.total_kg || 0); })
      .catch(e => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [distributorId]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return items;
    return items.filter(it =>
      (it.sku_code || "").toLowerCase().includes(query)
      || (it.sku_name || "").toLowerCase().includes(query));
  }, [items, q]);

  const startEdit = (it) => {
    setEditingId(it.id);
    setEditVal(String(Number(it.on_hand_kg || 0)));
  };

  const saveEdit = async (it) => {
    const val = parseFloat(editVal);
    if (isNaN(val) || val < 0) { toast.error("Enter a valid quantity"); return; }
    setSaving(true);
    try {
      // Edited in KG (what's shown on this card) — backend converts to
      // pieces server-side via the SKU's weight_per_pc_kg.
      await api.patch(`/distributor-stock/${it.id}`, { on_hand_kg: val });
      toast.success("Stock updated");
      setEditingId(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-4 pb-24 space-y-4" data-testid="distributor-stock-se-screen">
      <button onClick={() => nav(-1)} className="text-sm text-neutral-500 flex items-center gap-1">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div>
        <div className="text-[11px] uppercase tracking-widest text-neutral-400 flex items-center gap-1">
          <Boxes className="h-3.5 w-3.5" /> Distributor Stock
        </div>
        <h1 className="text-xl font-extrabold text-neutral-900">Current availability</h1>
      </div>

      {distributors.length > 1 && (
        <Select value={distributorId} onValueChange={setDistributorId}>
          <SelectTrigger data-testid="distributor-stock-se-picker"><SelectValue placeholder="Choose distributor…" /></SelectTrigger>
          <SelectContent>
            {distributors.map(d => (
              <SelectItem key={d.distributor_id} value={d.distributor_id}>
                {d.distributor_code} · {d.distributor_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {distributors.length === 0 && (
        <div className="py-10 text-center text-neutral-400 text-sm">
          No distributor is mapped to you yet.
        </div>
      )}

      {distributorId && (
        <>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
            <Input value={q} onChange={e => setQ(e.target.value)}
                   placeholder="Search SKU code / name…"
                   className="pl-9 h-10" data-testid="distributor-stock-se-search" />
          </div>

          <div className="rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-2 flex items-center justify-between"
               data-testid="distributor-stock-se-total-kg">
            <span className="text-xs uppercase tracking-widest text-neutral-500">Total KG</span>
            <span className="text-lg font-extrabold text-neutral-900">{Number(totalKg).toFixed(2)} kg</span>
          </div>

          {loading && (
            <div className="flex items-center gap-2 text-sm text-neutral-500 py-6 justify-center">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="py-10 text-center text-neutral-400 text-sm">No stock rows found.</div>
          )}

          <div className="space-y-2">
            {filtered.map(it => {
              const isEditing = editingId === it.id;
              return (
                <Card key={it.id} className="border-neutral-200 rounded-xl" data-testid={`distributor-stock-se-row-${it.sku_id}`}>
                  <CardContent className="p-3 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-[11px] text-neutral-400 font-mono">{it.sku_code}</div>
                      <div className="text-sm font-semibold text-neutral-900 truncate">{it.sku_name}</div>
                      {it.mrp != null && (
                        <div className="text-[11px] text-neutral-500">MRP ₹{it.mrp}</div>
                      )}
                    </div>
                    {isEditing ? (
                      <div className="flex items-center gap-1 shrink-0">
                        <Input type="number" min="0" step="0.01" value={editVal}
                               onChange={e => setEditVal(e.target.value)}
                               className="w-24 h-9 text-right"
                               data-testid={`distributor-stock-se-input-${it.sku_id}`} />
                        <span className="text-xs text-neutral-400">kg</span>
                        <Button size="icon" variant="ghost" disabled={saving} onClick={() => saveEdit(it)}
                                data-testid={`distributor-stock-se-save-${it.sku_id}`}>
                          <Check className="h-4 w-4 text-emerald-600" />
                        </Button>
                        <Button size="icon" variant="ghost" disabled={saving} onClick={() => setEditingId(null)}>
                          <X className="h-4 w-4 text-neutral-400" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 shrink-0">
                        <div className="text-right">
                          <div className="text-lg font-bold text-neutral-900">{Number(it.on_hand_kg || 0).toFixed(2)}</div>
                          <div className="text-[10px] uppercase tracking-widest text-neutral-400">kg on hand</div>
                        </div>
                        {canEdit && (
                          <Button size="icon" variant="ghost" onClick={() => startEdit(it)}
                                  data-testid={`distributor-stock-se-edit-${it.sku_id}`}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
